import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { surveyCompletionLabel } from '@aytm/app/stats/lib/page/viewer'

import { useStuckToTop } from '@aytm/app/stats/page/components/lib/hooks'
import { toHeaderStats } from '@aytm/app/stats/page/data-quality-report/lib/header-stats'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/components/styles/StickyHeader.module.scss'

const ValueLoader = () => (
  <span className={style['report-meta-value-loader']}>
    <span></span>
    <span></span>
    <span></span>
  </span>
)

// A quality layer that never landed carries null rather than 0, and dashes here like any other
// figure the report does not have.
const qualityFigure = value => (null == value ? '—' : formatAmounts(value))

const StickyHeader = ({
  mode,
  state,
  totals,
  dqrController,
  isReportLoading,
  isReportVirtual,
  isReportXpert,
  onFetch,
  onModeChange
}) => {
  const surveyId = PageGlobals.SURVEY_ID
  const surveyCompleted = surveyCompletionLabel()

  const dqrReport = dqrController?.getReport?.() || null
  const qualityStats = dqrReport ? toHeaderStats(dqrReport) : null
  // Terminal failure (error, no committed report) — the same signal the report body reads for
  // its LOAD_FAILED state. Without this the counters below would loop on the loader forever.
  const reportFailed = Boolean(dqrController?.hasError?.()) && !dqrReport

  const [isStuck, stickyRef] = useStuckToTop()

  // The /dqr report lands on the DQR controller — subscribe to the same seam the report body uses,
  // so the header swaps its loaders for real figures on the same notify.
  const [, forceRender] = useState(0)

  const onClick = useCallback(
    (event, mode) => {
      event.preventDefault()

      onModeChange(mode)
    },
    [onModeChange]
  )

  useEffect(() => dqrController?.subscribe?.(() => forceRender(n => n + 1)), [dqrController])

  return (
    <div ref={stickyRef} className={classNames(style.component, style[state], { [style.stuck]: isStuck })}>
      <div className={style['component-content']}>
        <div className={style['report-meta']}>
          <div className={style['report-meta-row']}>
            <span className={style['report-meta-title']}>N:</span>
            {isReportLoading ? (
              <ValueLoader />
            ) : (
              <span className={style['report-meta-value']}>
                {formatAmounts(totals.responses)}/{totals.respondents}
              </span>
            )}
            <span className={style['report-meta-title']}>ID:</span>
            <span className={style['report-meta-value']}>{surveyId}</span>
          </div>
          <div className={style['report-meta-row']}>
            <span className={style['report-meta-title']}>Completed:</span>
            <span className={style['report-meta-value']}>{surveyCompleted}</span>
          </div>
        </div>
        {'data-quality' == state ? (
          // The report runs on every report type, virtual and Xpert included, so its figures do too.
          <div className={style['report-quality']}>
            <div data-testid="stat-header-human-review-removals" className={style['report-quality-row']}>
              <span className={style['report-quality-title']}>Human Review removals:</span>
              {qualityStats ? (
                <span className={style['report-quality-value']}>{qualityFigure(qualityStats.rejectedCount)}</span>
              ) : reportFailed ? (
                <span className={style['report-quality-value']}>—</span>
              ) : (
                <ValueLoader />
              )}
            </div>
            <div data-testid="stat-header-blocked-attempts" className={style['report-quality-row']}>
              <span className={style['report-quality-title']}>Blocked survey attempts:</span>
              {qualityStats ? (
                <span className={style['report-quality-value']}>{qualityFigure(qualityStats.blockedCount)}</span>
              ) : reportFailed ? (
                <span className={style['report-quality-value']}>—</span>
              ) : (
                <ValueLoader />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={style['report-controls']} />
            {isReportVirtual || isReportXpert ? null : (
              <div className={style['report-switches']}>
                {'charts' == state ? null : (
                  <a
                    className={style['report-switches-button']}
                    href={`${window.location.href.replace(window.location.hash.split('/')[0], '#charts')}`}
                    rel="noreferrer"
                    onClick={event => onClick(event, 'charts')}
                  >
                    <i className="icon-aytmfd-quant-report-v2" />
                    <span>
                      Quant
                      <br />
                      report
                    </span>
                  </a>
                )}
                {['charts', 'specials'].includes(state) ? (
                  <>
                    {0 < totals.totalComments || 'comments' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-comments']}`,
                          { [style['report-switches-button-active']]: 'comments' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#comments')}`}
                        rel="noreferrer"
                        onClick={'comments' == mode ? null : event => onClick(event, 'comments')}
                      >
                        <i className="icon-aytmfd-comments-v2" />
                        <span>
                          {formatAmounts(totals.totalComments)}
                          <br />
                          {pluralize(totals.totalComments, 'verbatim', 'verbatims')}
                        </span>
                      </a>
                    ) : null}
                    {0 < totals.totalImageResponses || 'image-responses' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-image-responses']}`,
                          { [style['report-switches-button-active']]: 'image-responses' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#image-responses')}`}
                        rel="noreferrer"
                        onClick={'image-responses' == mode ? null : event => onClick(event, 'image-responses')}
                      >
                        <i className="icon-aytmfd-image-responses-v2" />
                        <span>
                          {formatAmounts(totals.totalImageResponses)} image
                          <br />
                          {pluralize(totals.totalImageResponses, 'response', 'responses')}
                        </span>
                      </a>
                    ) : null}
                    {0 < totals.totalVideoResponses || 'video-responses' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-video-responses']}`,
                          { [style['report-switches-button-active']]: 'video-responses' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#video-responses')}`}
                        rel="noreferrer"
                        onClick={'video-responses' == mode ? null : event => onClick(event, 'video-responses')}
                      >
                        <i className="icon-aytmfd-video-responses-v2" />
                        <span>
                          {formatAmounts(totals.totalVideoResponses)} video
                          <br />
                          {pluralize(totals.totalVideoResponses, 'response', 'responses')}
                        </span>
                      </a>
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
            <div className={style['report-refresh']}>
              <span
                className={classNames(style['report-refresh-button'], {
                  [style['report-refresh-button-spinning']]: isReportLoading
                })}
                aria-label="Refresh report"
                {...pressable(onFetch, { disabled: isReportLoading })}
              >
                <i className={classNames('icon-aytmfd-refresh-v2', { 'aytmfd-spin': isReportLoading })} />
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

StickyHeader.propTypes = {
  mode: PropTypes.string,
  state: PropTypes.string,
  totals: PropTypes.object,
  dqrController: PropTypes.object,
  isReportLoading: PropTypes.bool,
  isReportVirtual: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  onFetch: PropTypes.func,
  onModeChange: PropTypes.func
}

StickyHeader.displayName = 'StickyHeader'

export default StickyHeader
